import {
    PAGINATION_PAGE_PARAM,
    PAGINATION_PER_PAGE_PARAM,
    SearchParamController,
} from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import ReviewsDelegate from './reviews.delegate';
import { DEFAULT_PAGE_SIZE, DEFAULT_FILTERS, TIME_FRAME_OPTIONS } from './reviews.constants';
import { calculateDateRange } from '../../utils/dates';
import { message } from 'antd';
import dayjs from 'dayjs';

class ReviewsController extends Inherit(ClockoutController, ReviewsDelegate, SearchParamController) {
    async initialize() {
        const { initialPage, initialPerPage } = this.readPaginationFromUrl();
        const initialFilters = this.readFiltersFromUrl();

        this.setState(
            {
                filters: initialFilters,
            },
            () => {
                this.loadReviews(initialPage, initialPerPage);
            }
        );
    }

    readPaginationFromUrl() {
        const rawPage = parseInt(this.getSearchParamValue(PAGINATION_PAGE_PARAM), 10);
        const rawPerPage = parseInt(this.getSearchParamValue(PAGINATION_PER_PAGE_PARAM), 10);
        return {
            initialPage: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
            initialPerPage: Number.isFinite(rawPerPage) && rawPerPage > 0 ? rawPerPage : DEFAULT_PAGE_SIZE,
        };
    }

    readFiltersFromUrl() {
        const urlTimeFrame = this.getSearchParamValue('timeFrame');
        const validTimeFrameValues = TIME_FRAME_OPTIONS.map(opt => opt.value);
        const timeFrame = validTimeFrameValues.includes(urlTimeFrame) ? urlTimeFrame : DEFAULT_FILTERS.timeFrame;

        const customStart = this.getSearchParamValue('customStart');
        const customEnd = this.getSearchParamValue('customEnd');

        if (customStart && customEnd) {
            this.setState({ dateRange: [dayjs(customStart), dayjs(customEnd)] });
        }

        return {
            timeFrame,
            sortBy: this.getSearchParamValue('sortBy') || DEFAULT_FILTERS.sortBy,
            sortOrder: this.getSearchParamValue('sortOrder') || DEFAULT_FILTERS.sortOrder,
        };
    }

    async loadSummary() {
        try {
            this.setState({ loadingKPIs: true });
            const { timeFrame } = this.state.filters;
            const { dateRange } = this.state;

            const { start, end } = calculateDateRange(timeFrame, dateRange);

            const summaryParams = {
                period: timeFrame,
            };

            if (start) summaryParams.start = start;
            if (end) summaryParams.end = end;

            const summaryData = await this.data.fetchReviewsSummary(summaryParams);

            this.setState({
                summary: summaryData.summary || {},
                loadingKPIs: false,
            });
        } catch (error) {
            console.error('Error loading summary:', error);
            this.setState({ loadingKPIs: false });
        }
    }

    async loadList(page = 1, perPage = DEFAULT_PAGE_SIZE) {
        try {
            this.setState({ loadingTable: true });
            const { sortBy, sortOrder, timeFrame } = this.state.filters;
            const { dateRange } = this.state;

            const { start, end } = calculateDateRange(timeFrame, dateRange);

            const listParams = {
                page,
                perPage,
                sortBy,
                sortOrder,
                period: timeFrame,
            };

            if (start) listParams.start = start;
            if (end) listParams.end = end;

            const listData = await this.data.fetchReviews(listParams);

            this.setState({
                reviews: listData.items || [],
                pageInfo: {
                    current: page,
                    perPage,
                    total: listData.pageinfo?.count || 0,
                    hasNext: listData.pageinfo?.hasNext || false,
                    hasPrev: listData.pageinfo?.hasPrev || false,
                },
                loadingTable: false,
            });
        } catch (error) {
            console.error('Error loading list:', error);
            message.error('Failed to load reviews');
            this.setState({ loadingTable: false });
        }
    }

    async loadReviews(page = 1, perPage = DEFAULT_PAGE_SIZE) {
        await Promise.all([
            this.loadSummary(),
            this.loadList(page, perPage),
        ]);
    }

    handleTimeFrameChange(timeFrame) {
        this.setSearchParamValue('timeFrame', timeFrame);
        const nextDateRange = timeFrame === 'custom' ? this.state.dateRange : null;
        this.setState({ filters: { ...this.state.filters, timeFrame }, dateRange: nextDateRange }, async () => {
            if (timeFrame !== 'custom') {
                await this.loadReviews(1, this.state.pageInfo.perPage);
            } else if (nextDateRange?.[0] && nextDateRange?.[1]) {
                await this.loadReviews(1, this.state.pageInfo.perPage);
            }
        });
    }

    handleDateRangeChange(dates) {
        if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
            this.setState({ filters: { ...this.state.filters, timeFrame: '3m' }, dateRange: null }, async () => {
                this.setSearchParamValue('customStart', null);
                this.setSearchParamValue('customEnd', null);
                this.setSearchParamValue('timeFrame', '3m');
                await this.loadReviews(1, this.state.pageInfo.perPage);
            });
            return;
        }
        this.setSearchParamValue('customStart', dates[0].format('YYYY-MM-DD'));
        this.setSearchParamValue('customEnd', dates[1].format('YYYY-MM-DD'));
        this.setSearchParamValue('timeFrame', 'custom');
        this.setState({ filters: { ...this.state.filters, timeFrame: 'custom' }, dateRange: dates }, async () => {
            await this.loadReviews(1, this.state.pageInfo.perPage);
        });
    }
}

export default ReviewsController;
